import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import type { Character } from '../api/characters';

interface CharacterBottomSheetProps {
  character: Character | null;
  isOpen: boolean;
  onClose: () => void;
  clickPosition: { x: number; y: number } | null;
}

const CharacterBottomSheet: React.FC<CharacterBottomSheetProps> = ({ 
  character, 
  isOpen, 
  onClose,
  clickPosition 
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!character) return null;

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={onClose}
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 35 }}
            drag="y"
            dragDirectionLock
            dragConstraints={{ top: 0, bottom: 400 }}
            dragElastic={{ top: 0, bottom: 0.25 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose();
            }}
            className="fixed inset-x-0 bottom-0 z-[61] flex justify-center px-2 pb-2"
          >
            <div
              className="w-full max-w-lg bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[80vh]"
              dir="rtl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle */}
              <div className="pt-1 px-4 flex-shrink-0">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-1" />
                <div className="flex items-center justify-between -mt-1">
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="بستن"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                  <div className="w-8"></div>
                </div>
              </div>

              {/* Header with Avatar - Centered */}
              <div className="px-6 flex flex-col items-center flex-shrink-0">
                {/* Avatar with Shared Element */}
                <motion.div
                  layoutId={`character-avatar-${character.character_id}`}
                  className="flex-shrink-0 mb-2"
                  transition={{ 
                    type: 'spring', 
                    stiffness: 300, 
                    damping: 30 
                  }}
                >
                  <div className="w-[150px] h-[150px] rounded-full overflow-hidden border-4 border-primary-100 shadow-xl">
                    <img
                      src={character.icon}
                      alt={character.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + character.name;
                      }}
                    />
                  </div>
                </motion.div>

                {/* Character Name */}
                <h3 className="text-xl font-bold text-gray-900 font-persian text-center mb-2">
                  {character.name}
                </h3>
              </div>

              {/* Content Label */}
              <div className="px-6 pb-2 flex-shrink-0">
                <h4 className="text-sm font-bold text-gray-900 font-persian">
                  معرفی شخصیت:
                </h4>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <p className="text-sm text-gray-700 font-persian leading-loose text-justify whitespace-pre-line">
                  {character.description}
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CharacterBottomSheet;
